import ConversationSidePanelContent from "@app/components/assistant/conversation/ConversationSidePanelContent";
import {
  parseDataAsMessageIdAndActionId,
  useConversationSidePanelContext,
} from "@app/components/assistant/conversation/ConversationSidePanelContext";
import { DEFAULT_RIGHT_PANEL_SIZE } from "@app/components/assistant/conversation/constant";
import { useHashParam } from "@app/hooks/useHashParams";
import { useLockDocumentScroll } from "@app/hooks/useLockDocumentScroll";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import type { ConversationWithoutContentType } from "@app/types/assistant/conversation";
import {
  AGENT_ACTIONS_SIDE_PANEL_TYPE,
  ARTIFACT_PREVIEW_SIDE_PANEL_TYPE,
  FILE_PREVIEW_SIDE_PANEL_TYPE,
  FULL_SCREEN_HASH_PARAM,
  INTERACTIVE_CONTENT_SIDE_PANEL_TYPE,
  LIVE_DOC_SIDE_PANEL_TYPE,
} from "@app/types/conversation_side_panel";
import type { LightWorkspaceType } from "@app/types/user";
import {
  ArrowLeft,
  Button,
  cn,
  ResizableHandle,
  ResizablePanel,
} from "@dust-tt/sparkle";
import type React from "react";
import { useEffect, useRef } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

interface ConversationSidePanelContainerProps {
  conversation?: ConversationWithoutContentType;
  owner: LightWorkspaceType;
}

export default function ConversationSidePanelContainer({
  conversation,
  owner,
}: ConversationSidePanelContainerProps) {
  const {
    currentPanel,
    setPanelRef,
    onPanelClosed,
    registerPanelHost,
    backToDetails,
    data,
  } = useConversationSidePanelContext();
  // File previews, Frames, live docs and single-action views route their own back arrow to the drawer.
  const hasOwnBackButton =
    currentPanel === FILE_PREVIEW_SIDE_PANEL_TYPE ||
    currentPanel === ARTIFACT_PREVIEW_SIDE_PANEL_TYPE ||
    currentPanel === INTERACTIVE_CONTENT_SIDE_PANEL_TYPE ||
    currentPanel === LIVE_DOC_SIDE_PANEL_TYPE ||
    (currentPanel === AGENT_ACTIONS_SIDE_PANEL_TYPE &&
      !!parseDataAsMessageIdAndActionId(data).actionId);
  const backBarAction = hasOwnBackButton ? undefined : backToDetails;
  const panelRef = useRef<ImperativePanelHandle | null>(null);
  const [fullScreenHash] = useHashParam(FULL_SCREEN_HASH_PARAM);
  const isFullScreen = fullScreenHash === "true";

  const isMobile = useIsMobile();
  const isMobilePanelOpen = isMobile && !!currentPanel;

  useLockDocumentScroll(isMobilePanelOpen);

  useEffect(() => registerPanelHost(), [registerPanelHost]);

  useEffect(() => {
    if (isMobile) {
      setPanelRef(null);
      return;
    }

    setPanelRef(panelRef.current);
  }, [isMobile, setPanelRef]);

  useEffect(() => {
    if (isMobile || !currentPanel || !panelRef.current) {
      return;
    }

    panelRef.current?.expand(DEFAULT_RIGHT_PANEL_SIZE);
  }, [currentPanel, isMobile]);

  if (isMobile) {
    if (!currentPanel || !conversation) {
      return null;
    }

    return (
      <div className="fixed inset-0 z-50 flex flex-col overflow-hidden overscroll-none bg-panel-background">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain">
          <PanelContentWithBack onBack={backBarAction}>
            <ConversationSidePanelContent
              conversation={conversation}
              owner={owner}
              currentPanel={currentPanel}
            />
          </PanelContentWithBack>
        </div>
      </div>
    );
  }

  return (
    <>
      {!!conversation && (
        <ResizableHandle
          withHandle={currentPanel && !isFullScreen}
          disabled={!currentPanel || isFullScreen}
          className="z-50 bg-transparent"
        />
      )}
      {/* Panel Container - either Interactive Content or Actions */}
      <ResizablePanel
        ref={panelRef}
        minSize={20}
        defaultSize={0}
        onTransitionEnd={() => {
          if (panelRef.current?.isCollapsed()) {
            onPanelClosed();
          }
        }}
        collapsible
        collapsedSize={0}
        className={cn(
          // Opening and widening glide; a drag on the handle follows the pointer with no lag.
          "flex-0 overflow-hidden transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none [[data-resize-handle-state=drag]+&]:transition-none",
          !currentPanel && "hidden w-0 md:block",
          // On mobile: overlay full screen with absolute positioning.
          "md:relative",
          currentPanel &&
            "absolute inset-0 bg-panel-background md:relative md:inset-auto md:bg-transparent md:py-3 md:pl-2 md:pr-3"
        )}
      >
        {currentPanel && conversation && (
          <div className="floating-layer flex h-full flex-col overflow-hidden bg-modal-background md:rounded-2xl md:border md:border-border md:shadow-xl">
            <PanelContentWithBack onBack={backBarAction}>
              <ConversationSidePanelContent
                conversation={conversation}
                owner={owner}
                currentPanel={currentPanel}
              />
            </PanelContentWithBack>
          </div>
        )}
      </ResizablePanel>
    </>
  );
}

function PanelContentWithBack({
  children,
  onBack,
}: {
  children: React.ReactNode;
  onBack: (() => void) | undefined;
}) {
  if (!onBack) {
    return children;
  }

  // Panels size themselves to the full panel height; the child override keeps them
  // from overflowing below the back bar.
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border bg-panel-background px-2 py-1">
        <Button
          variant="ghost-secondary"
          size="xs"
          icon={ArrowLeft}
          label="In this chat"
          onClick={onBack}
        />
      </div>
      <div className="min-h-0 flex-1 [&>*]:h-full">{children}</div>
    </div>
  );
}
